import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { packListsApi, packingAssignmentsApi, marketsApi, marketDaysApi, kegRecordsApi } from '../../services/api';
import './MarketPackSheet.css';

export default function MarketPackSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packList, setPackList] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [plData, itemData, assignData, marketData, mdData] = await Promise.all([
          packListsApi.get(id),
          packListsApi.getItems(id),
          packingAssignmentsApi.getAll(),
          marketsApi.getAll(),
          marketDaysApi.getAll(),
        ]);

        const pl = plData.pack_list;
        const items = itemData.items || [];
        const assignments = assignData.assignments || [];
        const markets = marketData.markets || [];
        const allMarketDays = mdData.market_days || [];

        setPackList(pl);

        const sheets = [];

        for (const assignment of assignments) {
          if (!assignment.sunday_market_id || assignment.sunday_market_id === 'none' || assignment.sunday_market_id === '') continue;

          const satMarket = markets.find(m => String(m.id) === String(assignment.saturday_market_id));
          const sunMarket = markets.find(m => String(m.id) === String(assignment.sunday_market_id));
          if (!satMarket || !sunMarket) continue;

          const sheetItems = items.filter(i => String(i.market_id) === String(assignment.saturday_market_id));
          if (sheetItems.length === 0) continue;

          // Find market day for this Saturday market around the pack list week
          const weekDate = new Date(pl.week_date);
          const satMarketDay = allMarketDays.find(md => {
            const mdDate = new Date(md.date);
            const diffDays = Math.abs((mdDate - weekDate) / (1000 * 60 * 60 * 24));
            return String(md.market_id) === String(assignment.saturday_market_id) && diffDays <= 7;
          });

          // Get keg records for this market day
          let kegs = [];
          if (satMarketDay) {
            const kegData = await kegRecordsApi.getForMarketDay(satMarketDay.id);
            kegs = kegData.keg_records || [];
          }

          sheets.push({ saturdayMarket: satMarket, sundayMarket: sunMarket, items: sheetItems, kegs });
        }

        setGroups(sheets);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  if (loading) return <div style={{padding: 48}}>Loading...</div>;
  if (error) return <div style={{padding: 48, color: 'red'}}>{error}</div>;

  return (
    <div className="market-pack-sheet">
      <div className="sheet-toolbar no-print">
        <button className="back-btn" onClick={() => navigate(`/ops/pack-lists/${id}`)}>Back to Pack List</button>
        <button className="btn-primary" onClick={() => window.print()}>Print All Sheets</button>
      </div>

      {groups.length === 0 ? (
        <div className="no-print" style={{padding: 48, textAlign: 'center', color: '#999'}}>
          No packing assignments found. Set up Saturday to Sunday assignments in Admin - Packing Assignments.
        </div>
      ) : (
        groups.map(group => (
          <div key={group.saturdayMarket.id} className="pack-sheet-page">
            <div className="sheet-header">
              <div className="sheet-title">
                <h1>{group.saturdayMarket.name} - Packing for {group.sundayMarket.name}</h1>
                <p>Week of {packList ? new Date(packList.week_date).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'}) : ''} - AR Pack Sheet - Printed: {new Date().toLocaleDateString()}</p>
              </div>
              <div className="sheet-logo">
                <span className="logo-arf">ARF</span>
              </div>
            </div>

            <table className="sheet-table">
              <thead>
                <tr>
                  <th className="col-product">Product</th>
                  <th className="col-qty">Qty to Pack</th>
                  <th className="col-actual">Actual Packed</th>
                  <th className="col-notes">Notes</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map(item => (
                  <tr key={item.id}>
                    <td className="col-product">{item.product_name}</td>
                    <td className="col-qty">{item.qty_confirmed || '-'}</td>
                    <td className="col-actual"></td>
                    <td className="col-notes"></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {group.kegs.length > 0 && (
              <div className="keg-section">
                <h3 className="keg-section-title">Kegs</h3>
                <table className="sheet-table">
                  <thead>
                    <tr>
                      <th className="col-product">Flavor</th>
                      <th className="col-qty">Start Weight (lbs)</th>
                      <th className="col-actual">End of Day Weight (lbs)</th>
                      <th className="col-notes">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.kegs.map(keg => (
                      <tr key={keg.id}>
                        <td className="col-product">{keg.flavor}</td>
                        <td className="col-qty">{keg.start_weight || '-'}</td>
                        <td className="col-actual"></td>
                        <td className="col-notes"></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="sheet-footer">
              <div className="footer-field">Packed by: _______________________</div>
              <div className="footer-field">Time: _______________________</div>
              <div className="footer-field">Checked by: _______________________</div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
